import { useState, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Switch } from "./ui/switch";
import { 
  Upload, 
  Wand2, 
  Image as ImageIcon, 
  Video, 
  Sparkles, 
  Download,
  Share2,
  RefreshCw,
  Loader2,
  X,
  Play,
  Pause,
  ChevronLeft,
  Settings2,
  Zap
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const GolfGifGenerator = ({ onBack, onShare }) => {
  // Generation mode
  const [generationMode, setGenerationMode] = useState("ai-video"); // ai-video, ai-animate, enhance
  
  // Input states
  const [description, setDescription] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [uploadedFileType, setUploadedFileType] = useState(null); // 'image' or 'video'
  
  // Display options
  const [gifSize, setGifSize] = useState("medium"); // small, medium, large
  const [loopStyle, setLoopStyle] = useState("forever"); // forever, bounce, once
  const [speed, setSpeed] = useState("normal"); // slow, normal, fast
  
  // Output states
  const [generatedGifUrl, setGeneratedGifUrl] = useState(null);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  
  const fileInputRef = useRef(null);

  // Golf-themed prompt suggestions
  const promptSuggestions = [
    "Epic hole-in-one celebration with confetti",
    "Golfer doing a victory dance on the green",
    "Golf ball rolling into the hole in slow motion",
    "Frustrated golfer throwing club into water hazard",
    "Perfect golf swing in sunset lighting",
    "Golf cart racing across the fairway",
    "Caddy giving enthusiastic fist bump",
    "Ball bouncing off tree back onto fairway"
  ];

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (!isImage && !isVideo) {
      toast.error("Please upload an image or video file");
      return;
    }

    // Size limit: 50MB for videos, 10MB for images
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${isVideo ? '50MB' : '10MB'}`);
      return;
    }

    setUploadedFile(file);
    setUploadedFileType(isImage ? 'image' : 'video');
    setUploadedFileUrl(URL.createObjectURL(file));
  };

  const clearUpload = () => {
    setUploadedFile(null);
    setUploadedFileUrl(null);
    setUploadedFileType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const [currentJobId, setCurrentJobId] = useState(null);
  const [canNavigateAway, setCanNavigateAway] = useState(false);

  const handleGenerate = async () => {
    if (!description.trim() && generationMode === "ai-video") {
      toast.error("Please enter a description for your GIF");
      return;
    }

    if ((generationMode === "ai-animate" || generationMode === "enhance") && !uploadedFile) {
      toast.error("Please upload an image or video first");
      return;
    }

    setIsGenerating(true);
    setGeneratedGifUrl(null);
    setGeneratedVideoUrl(null);
    setCanNavigateAway(false);

    try {
      // Get auth token
      const token = localStorage.getItem('parseeker_session_token');
      const headers = token ? { 'X-Session-Token': token } : {};

      if (generationMode === "ai-video") {
        // Pure AI video generation with background processing
        setGenerationProgress("Starting AI video generation...");
        
        // Start the job
        const startResponse = await fetch(`${API}/gif/generate-ai-video`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({
            prompt: description,
            size: gifSize,
            loop_style: loopStyle,
            speed: speed,
            golf_theme: true
          })
        });

        if (!startResponse.ok) {
          const err = await startResponse.json();
          throw new Error(err.detail || 'Failed to start generation');
        }

        const { job_id } = await startResponse.json();
        setCurrentJobId(job_id);
        setGenerationProgress("Generating video with AI... This takes 2-5 minutes.");
        setCanNavigateAway(true);
        
        toast.success("GIF generation started! You can navigate away - we'll notify you when it's ready.", {
          duration: 5000
        });
        
        // Poll for status (user can stay or leave)
        let attempts = 0;
        const maxAttempts = 120; // 10 minutes max (5 second intervals)
        
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
          
          const statusResponse = await fetch(`${API}/gif/status/${job_id}`);
          if (!statusResponse.ok) {
            throw new Error('Failed to check job status');
          }
          
          const status = await statusResponse.json();
          setGenerationProgress(status.progress || "Processing...");
          
          if (status.status === "completed") {
            setGeneratedVideoUrl(status.video_url);
            setGeneratedGifUrl(status.gif_url);
            setCanNavigateAway(false);
            toast.success("GIF generated successfully!");
            break;
          } else if (status.status === "failed") {
            throw new Error(status.error || "Generation failed");
          }
          
          attempts++;
        }
        
        if (attempts >= maxAttempts) {
          throw new Error("Generation timed out. Check notifications for updates.");
        }
        
      } else if (generationMode === "ai-animate") {
        // Animate uploaded image
        const formData = new FormData();
        formData.append("file", uploadedFile);
        formData.append("description", description);
        formData.append("size", gifSize);
        formData.append("loop_style", loopStyle);
        formData.append("speed", speed);
        
        setGenerationProgress("Animating your image into a golf GIF...");

        const response = await fetch(`${API}/gif/animate-image`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Animation failed');
        }

        const data = await response.json();
        setGeneratedGifUrl(data.gif_url);
        toast.success("GIF generated successfully!");
        
      } else if (generationMode === "enhance") {
        // Enhance and convert to GIF
        const formData = new FormData();
        formData.append("file", uploadedFile);
        formData.append("description", description);
        formData.append("size", gifSize);
        formData.append("loop_style", loopStyle);
        formData.append("speed", speed);
        
        setGenerationProgress("Enhancing and converting to GIF...");

        const response = await fetch(`${API}/gif/enhance-to-gif`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.detail || 'Enhancement failed');
        }

        const data = await response.json();
        setGeneratedGifUrl(data.gif_url);
        toast.success("GIF generated successfully!");
      }
      
    } catch (err) {
      console.error("GIF generation error:", err);
      toast.error(err.message || "Failed to generate GIF");
    } finally {
      setIsGenerating(false);
      setGenerationProgress("");
    }
  };

  const handleDownload = async () => {
    if (!generatedGifUrl) return;
    
    try {
      const response = await fetch(generatedGifUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `par-seeker-golf-gif-${Date.now()}.gif`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("GIF downloaded!");
    } catch (err) {
      toast.error("Failed to download GIF");
    }
  };

  const handleShareToFeed = () => {
    if (onShare && generatedGifUrl) {
      onShare(generatedGifUrl, 'feed');
      toast.success("Opening share dialog...");
    }
  };

  const handleShareToChat = () => {
    if (onShare && generatedGifUrl) {
      onShare(generatedGifUrl, 'chat');
      toast.success("Opening chat selector...");
    }
  };

  const getSizeLabel = () => {
    switch(gifSize) {
      case 'small': return '320x180';
      case 'medium': return '480x270';
      case 'large': return '720x405';
      default: return '480x270';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0a] to-[#1a1a1a] text-white p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onBack}
              className="text-gray-400 hover:text-white"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-green-500" />
              Golf GIF Generator
            </h1>
            <p className="text-gray-400 text-sm">Create epic golf moments</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowSettings(!showSettings)}
          className={showSettings ? "text-green-500" : "text-gray-400"}
        >
          <Settings2 className="w-5 h-5" />
        </Button>
      </div>

      <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Input */}
        <div className="space-y-4">
          {/* Generation Mode Tabs */}
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white">Generation Mode</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={generationMode} onValueChange={setGenerationMode}>
                <TabsList className="grid grid-cols-3 bg-[#0a0a0a]">
                  <TabsTrigger 
                    value="ai-video" 
                    className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-xs"
                  >
                    <Wand2 className="w-3 h-3 mr-1" />
                    AI Video
                  </TabsTrigger>
                  <TabsTrigger 
                    value="ai-animate"
                    className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-xs"
                  >
                    <Zap className="w-3 h-3 mr-1" />
                    Animate
                  </TabsTrigger>
                  <TabsTrigger 
                    value="enhance"
                    className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-xs"
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    Enhance
                  </TabsTrigger>
                </TabsList>

                <div className="mt-4">
                  <TabsContent value="ai-video" className="mt-0">
                    <p className="text-gray-400 text-sm mb-3">
                      Describe your dream golf GIF and AI will create it from scratch using Sora 2.
                    </p>
                  </TabsContent>
                  <TabsContent value="ai-animate" className="mt-0">
                    <p className="text-gray-400 text-sm mb-3">
                      Upload a golf photo and AI will animate it into a moving GIF.
                    </p>
                  </TabsContent>
                  <TabsContent value="enhance" className="mt-0">
                    <p className="text-gray-400 text-sm mb-3">
                      Upload a photo/video and convert it to an optimized golf GIF with effects.
                    </p>
                  </TabsContent>
                </div>
              </Tabs>
            </CardContent>
          </Card>

          {/* Description Input */}
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white">
                {generationMode === "ai-video" ? "Describe Your GIF" : "Add Description (Optional)"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                placeholder={generationMode === "ai-video" 
                  ? "Describe the golf scene you want to create..." 
                  : "Add context or effects you want applied..."
                }
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="bg-[#0a0a0a] border-gray-700 text-white min-h-[100px] resize-none"
              />
              
              {generationMode === "ai-video" && (
                <div>
                  <Label className="text-xs text-gray-500 mb-2 block">Quick Ideas:</Label>
                  <div className="flex flex-wrap gap-2">
                    {promptSuggestions.slice(0, 4).map((suggestion, i) => (
                      <button
                        key={i}
                        onClick={() => setDescription(suggestion)}
                        className="text-xs px-2 py-1 bg-[#0a0a0a] border border-gray-700 rounded-full text-gray-400 hover:text-green-400 hover:border-green-600 transition-colors"
                      >
                        {suggestion.slice(0, 30)}...
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* File Upload (for animate and enhance modes) */}
          {(generationMode === "ai-animate" || generationMode === "enhance") && (
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white">Upload Media</CardTitle>
              </CardHeader>
              <CardContent>
                {!uploadedFile ? (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center cursor-pointer hover:border-green-600 transition-colors"
                  >
                    <Upload className="w-10 h-10 mx-auto text-gray-500 mb-3" />
                    <p className="text-gray-400">
                      Click to upload {generationMode === "ai-animate" ? "an image" : "image or video"}
                    </p>
                    <p className="text-gray-600 text-xs mt-1">
                      {generationMode === "ai-animate" ? "PNG, JPG up to 10MB" : "PNG, JPG, MP4 up to 50MB"}
                    </p>
                  </div>
                ) : (
                  <div className="relative">
                    {uploadedFileType === 'image' ? (
                      <img 
                        src={uploadedFileUrl} 
                        alt="Uploaded" 
                        className="w-full rounded-lg max-h-48 object-cover"
                      />
                    ) : (
                      <video 
                        src={uploadedFileUrl} 
                        className="w-full rounded-lg max-h-48 object-cover"
                        controls
                      />
                    )}
                    <button
                      onClick={clearUpload}
                      className="absolute top-2 right-2 p-1 bg-black/70 rounded-full text-white hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={generationMode === "ai-animate" ? "image/*" : "image/*,video/*"}
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </CardContent>
            </Card>
          )}

          {/* Display Options */}
          {showSettings && (
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white">Display Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Size */}
                <div>
                  <Label className="text-gray-400 text-sm">GIF Size</Label>
                  <Select value={gifSize} onValueChange={setGifSize}>
                    <SelectTrigger className="bg-[#0a0a0a] border-gray-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-gray-700">
                      <SelectItem value="small">Small (320x180)</SelectItem>
                      <SelectItem value="medium">Medium (480x270)</SelectItem>
                      <SelectItem value="large">Large (720x405)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Loop Style */}
                <div>
                  <Label className="text-gray-400 text-sm">Loop Style</Label>
                  <Select value={loopStyle} onValueChange={setLoopStyle}>
                    <SelectTrigger className="bg-[#0a0a0a] border-gray-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-gray-700">
                      <SelectItem value="forever">Loop Forever</SelectItem>
                      <SelectItem value="bounce">Bounce (Forward/Backward)</SelectItem>
                      <SelectItem value="once">Play Once</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Speed */}
                <div>
                  <Label className="text-gray-400 text-sm">Speed</Label>
                  <Select value={speed} onValueChange={setSpeed}>
                    <SelectTrigger className="bg-[#0a0a0a] border-gray-700 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-gray-700">
                      <SelectItem value="slow">Slow Motion (0.5x)</SelectItem>
                      <SelectItem value="normal">Normal (1x)</SelectItem>
                      <SelectItem value="fast">Fast (1.5x)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || (generationMode === "ai-video" && !description.trim()) || ((generationMode === "ai-animate" || generationMode === "enhance") && !uploadedFile)}
            className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white font-semibold py-6 text-lg"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5 mr-2" />
                Generate Golf GIF
              </>
            )}
          </Button>

          {isGenerating && generationProgress && (
            <div className="text-center space-y-2">
              <p className="text-gray-400 text-sm animate-pulse">{generationProgress}</p>
              {canNavigateAway && (
                <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-3">
                  <p className="text-green-400 text-xs">
                    ✨ You can navigate away! We'll notify you when your GIF is ready.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Preview */}
        <div className="space-y-4">
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-white">Preview</CardTitle>
              <CardDescription className="text-gray-500">
                {generatedGifUrl ? "Your generated GIF" : "Your GIF will appear here"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="aspect-video bg-[#0a0a0a] rounded-lg flex items-center justify-center overflow-hidden">
                {isGenerating ? (
                  <div className="text-center">
                    <div className="relative w-20 h-20 mx-auto mb-4">
                      <div className="absolute inset-0 border-4 border-green-500/30 rounded-full"></div>
                      <div className="absolute inset-0 border-4 border-transparent border-t-green-500 rounded-full animate-spin"></div>
                      <Sparkles className="absolute inset-0 m-auto w-8 h-8 text-green-500" />
                    </div>
                    <p className="text-gray-400 text-sm">Creating magic...</p>
                  </div>
                ) : generatedGifUrl ? (
                  <img 
                    src={generatedGifUrl} 
                    alt="Generated GIF" 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : generatedVideoUrl ? (
                  <video 
                    src={generatedVideoUrl} 
                    className="max-w-full max-h-full object-contain"
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  <div className="text-center text-gray-600">
                    <Video className="w-16 h-16 mx-auto mb-3 opacity-30" />
                    <p>No GIF generated yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          {(generatedGifUrl || generatedVideoUrl) && (
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={handleDownload}
                    variant="outline"
                    className="border-gray-700 text-white hover:bg-gray-800"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                  <Button
                    onClick={() => {
                      setGeneratedGifUrl(null);
                      setGeneratedVideoUrl(null);
                    }}
                    variant="outline"
                    className="border-gray-700 text-white hover:bg-gray-800"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    New GIF
                  </Button>
                </div>
                
                <div className="mt-3 pt-3 border-t border-gray-800">
                  <Label className="text-gray-500 text-xs mb-2 block">Share To:</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={handleShareToFeed}
                      className="bg-green-600 hover:bg-green-500 text-white"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Social Feed
                    </Button>
                    <Button
                      onClick={handleShareToChat}
                      className="bg-blue-600 hover:bg-blue-500 text-white"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Chat/DM
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tips Card */}
          <Card className="bg-gradient-to-br from-green-900/20 to-green-800/10 border-green-800/30">
            <CardContent className="pt-4">
              <h4 className="text-green-400 font-medium mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Pro Tips
              </h4>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>• Be specific about camera angles and lighting</li>
                <li>• Include emotions like "celebration" or "frustration"</li>
                <li>• Mention golf-specific elements: green, fairway, bunker</li>
                <li>• AI videos take 2-5 minutes - worth the wait!</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GolfGifGenerator;
